/*
 * Shared delivery-date maths.
 *
 * Two snippets need the same numbers: product-delivery-info-sb prints the
 * expected window as a sentence, delivery-status-sb prints it as a three-row
 * timeline. When each carried its own copy of these helpers they drifted, and
 * one product page ended up quoting two different delivery windows.
 *
 * Everything here is deliberately dependency-free and side-effect-free.
 */
window.SBDelivery = (function () {
  var MONTHS = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  /**
   * Pull a working-day range out of the free-text delivery metafield.
   *
   *   "1-3 Werktage"   -> {min: 1,  max: 3}
   *   "3 Werktage"     -> {min: 3,  max: 3}
   *   "ca. 2 Wochen"   -> {min: 14, max: 14}
   *   "3-4 Wochen"     -> {min: 21, max: 28}
   *   "1-2 Monate"     -> {min: 30, max: 60}
   *
   * The unit matters and used to be ignored: "2 Wochen" was read as two days,
   * so a two-week delivery was quoted as arriving the day after tomorrow.
   *
   * A week counts as SEVEN working days, not five. Read literally, "3 Wochen"
   * is 21 days and five-per-week would make that 15 working days - which from a
   * Monday lands on exactly the same date, because the weekends skipped are the
   * weekends that elapse. The store means something stronger: 21 days of
   * shipping activity, weekends not counted at all, so "3 Wochen" resolves
   * around four calendar weeks out. Months follow the same rule at 30.
   *
   * Both numbers are the whole of that policy. Changing 7 back to 5 restores
   * the literal calendar reading; nothing else needs touching.
   *
   * Returns null when there is no number to work with.
   */
  var WEEK_DAYS = 7;
  var MONTH_DAYS = 30;

  //          German          Swiss dial.   English    French       Italian
  var WEEK_WORDS  = /woche|wuche|week|semaine|settiman/;
  var MONTH_WORDS = /monat|month|mois|mese|mesi/;

  function parseRaw(raw) {
    var text = (raw || '').toLowerCase();
    var matches = text.match(/\d+/g);
    if (!matches || !matches.length) return null;

    var min = parseInt(matches[0], 10);
    var max = matches.length > 1 ? parseInt(matches[1], 10) : min;
    if (isNaN(min) || min < 0) return null;
    if (isNaN(max) || max < min) max = min;

    // Months before weeks before days, so a string naming more than one unit
    // resolves to the coarsest.
    //
    // Several languages on purpose: the metafield is typed by hand and supplier
    // text often arrives in English. Anything unrecognised falls through to
    // days, and that failure is silent — "2 weeks" read as two days quoted a
    // delivery for the day after tomorrow. Add a form here rather than relying
    // on everyone writing German.
    var multiplier = 1;
    if (MONTH_WORDS.test(text)) {
      multiplier = MONTH_DAYS;
    } else if (WEEK_WORDS.test(text)) {
      multiplier = WEEK_DAYS;
    }

    return { min: min * multiplier, max: max * multiplier };
  }

  /**
   * "Werktage" means working days, so Saturdays and Sundays do not count,
   * and a result landing on one is pushed to the following Monday.
   *
   * Swiss public holidays are NOT accounted for — deliberately, for now. Around
   * 1 August or Christmas the quoted date will therefore run a day or two
   * early. If that needs fixing, this is the only place to change: the fixed
   * dates are easy, but Karfreitag / Ostermontag / Auffahrt / Pfingstmontag
   * move with Easter and need computing per year.
   */
  function addBusinessDays(from, days) {
    var d = new Date(from.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var w = d.getDay();
      if (w !== 0 && w !== 6) added++;
    }
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    return d;
  }

  /**
   * "19 August".
   *
   * de-CH would render the day as "19." — the dot is the German ordinal
   * marker, which this design drops. The day and month are taken as separate
   * parts and rejoined rather than stripping dots out of a formatted string,
   * which would also hit a dot inside an abbreviated month name.
   */
  function fmt(d) {
    try {
      var day = '', month = '';
      new Intl.DateTimeFormat('de-CH', { day: 'numeric', month: 'long' })
        .formatToParts(d)
        .forEach(function (part) {
          if (part.type === 'day') day = part.value;
          if (part.type === 'month') month = part.value;
        });
      if (day && month) return day + ' ' + month;
    } catch (e) {}
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  /** Same month collapses to "19–20 August" instead of naming it twice. */
  function range(a, b) {
    if (a.getTime() === b.getTime()) return fmt(a);
    if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
      return a.getDate() + '–' + fmt(b);
    }
    return fmt(a) + ' – ' + fmt(b);
  }

  /**
   * Resolve a delivery window and return the formatted dates.
   *
   * Returns null when `raw` holds no usable number. Callers decide what that
   * means: a card with nothing to quote should say nothing rather than invent a
   * window from a global default, which is how a product whose metafield read
   * "auf Anfrage" ended up promising a date.
   *
   * `fallback` is optional. Pass {min, max} only where a guess is genuinely
   * better than silence.
   */
  function windowFor(raw, fallback) {
    var parsed = parseRaw(raw);
    if (!parsed && !fallback) return null;
    var min = parsed ? parsed.min : fallback.min;
    var max = parsed ? parsed.max : fallback.max;
    var today = new Date();
    var from = addBusinessDays(today, min);
    var to = addBusinessDays(today, max);
    return { min: min, max: max, from: from, to: to, text: range(from, to) };
  }

  /**
   * What to show for a raw metafield value, in one call.
   *
   *   "6-8 Werktage" -> { kind: 'window', text: '27-31 August' }
   *   "Sofort"       -> { kind: 'text',   text: 'Sofort' }
   *   ""             -> { kind: 'none' }
   *
   * windowFor returns null for anything without a number, and callers took
   * that to mean "say nothing" - so a product marked Sofort showed no delivery
   * line at all, while the old theme printed the word. Passing the merchant's
   * own words through is not the same thing as inventing a date from a
   * default, which is the mistake windowFor exists to prevent: nothing is
   * calculated here, it is quoted.
   */
  function describe(raw) {
    var text = String(raw == null ? '' : raw).trim();
    if (!text) return { kind: 'none', text: '' };

    var win = windowFor(text);
    if (win) return { kind: 'window', text: win.text, window: win };

    return { kind: 'text', text: text };
  }

  return {
    parseRaw: parseRaw,
    addBusinessDays: addBusinessDays,
    fmt: fmt,
    range: range,
    windowFor: windowFor,
    describe: describe
  };
})();
